import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useShopSettingsLoad } from './useShopSettingsLoad';
import AnalyticsPreferences from '../AnalyticsPreferences';
import BillingCard from '../BillingCard';
import OwnerShell, { OwnerLoadingShell } from '../OwnerShell';
import CoralHeader from '../../dashboard/CoralHeader';

/**
 * Last of four owner-settings sub-screens (Task 13 of the ShopSettings
 * split). Unlike StorefrontSettings/RewardsSettings/CounterSettings, this
 * screen has NO group save button — both children manage their own saves
 * (AnalyticsPreferences calls updateShopSettings itself and shows its own
 * SAVE PREFERENCES button; BillingCard is links/status with nothing to
 * persist locally). This is the sanctioned exemption from one-save-per-screen
 * because neither child exposes an external save API for a parent to drive.
 *
 * Billing MOVES here from ShopOwnerDashboard (Task 13 brief) — it no longer
 * renders on the owner dashboard.
 */
function AccountSettings() {
    const navigate = useNavigate();
    const { loading, shopId, shopName } = useShopSettingsLoad();

    if (loading) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="Loading…">
                <div className="space-y-3.5">
                    <div className="bg-white rounded-[10px] h-24 animate-pulse" />
                    <div className="bg-white rounded-[10px] h-64 animate-pulse" />
                </div>
            </OwnerLoadingShell>
        );
    }

    return (
        <OwnerShell
            active="setup"
            header={<CoralHeader statusLabel="SETUP" eyebrow="ACCOUNT SETTINGS" title={shopName} />}
        >
        <div className="space-y-3.5 pb-8">
            {/* sr-only: CoralHeader's title already shows the shop name visually
                (mockup drops the repeated in-body page title) — this keeps one
                real heading per page for assistive tech / the ACCOUNT test. */}
            <h1 className="sr-only">Account</h1>

            <button
                onClick={() => navigate('/shops/settings')}
                className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
            >
                <ArrowLeft size={13} /> Settings
            </button>

            {/* Analytics Preferences — self-contained, owns its own load + save. */}
            <AnalyticsPreferences />

            {/* Billing — moved off the owner dashboard. Needs a resolved shopId
                (BillingCard's useShopTier query is disabled without one). */}
            {shopId && <BillingCard shopId={shopId} />}
        </div>
        </OwnerShell>
    );
}

export default AccountSettings;
